import { useEffect, useMemo, useState } from "react";
import { api, pct } from "../api";
import ExportBar from "../components/ExportBar";

export default function Streams() {
  const [data, setData] = useState(null);
  const [portfolio, setPortfolio] = useState("");

  useEffect(() => { api.get("/data/latest").then((r) => setData(r.data)); }, []);

  const allStreams = useMemo(
    () => (data?.streams || []).slice().sort((a, b) => b.total - a.total),
    [data]
  );

  const streams = useMemo(() => {
    if (!portfolio) return allStreams;
    return allStreams.filter((s) => (s.portfolio || "Unassigned") === portfolio);
  }, [allStreams, portfolio]);

  const portfolioOptions = useMemo(
    () => allStreams.map((s) => s.portfolio).filter(Boolean),
    [allStreams]
  );

  if (!data) return <div className="text-slate-500">Loading…</div>;
  if (data.empty) return <div className="bg-white p-8 rounded-xl text-center text-slate-500">No data.</div>;

  return (
    <div data-testid="streams-page">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-xl font-bold" style={{ color: "#1a3666" }}>
            Streams (Product Portfolios) <span className="text-xs font-normal text-slate-500">({streams.length} of {allStreams.length})</span>
          </h2>
          <select value={portfolio} onChange={(e) => setPortfolio(e.target.value)}
                  data-testid="st-filter-portfolio"
                  className="px-2 py-1 text-xs border border-slate-300 rounded-md bg-white">
            <option value="">All Portfolios</option>
            {portfolioOptions.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          {portfolio && (
            <button onClick={() => setPortfolio("")} data-testid="st-filter-clear"
                    className="text-xs text-slate-500 hover:text-slate-900 underline">Clear</button>
          )}
        </div>
        <ExportBar spec={() => ({
          title: "Streams",
          subtitle: portfolio ? `Portfolio: ${portfolio}` : undefined,
          filters: { portfolio: portfolio || "" },
          columns: [
            { key: "portfolio", label: "Portfolio" },
            { key: "total", label: "Team Size" },
            { key: "optimal", label: "Optimal" },
            { key: "overload", label: "Overloaded" },
            { key: "underload", label: "Underloaded" },
            { key: "unassigned", label: "Idle" },
            { key: "avgUtil", label: "Avg Util %", get: (r) => pct(r.avgUtil) },
            { key: "leads", label: "Leads" },
            { key: "cc", label: "CCs" },
          ],
          rows: streams,
          filename: portfolio ? `vSDI_Streams_${portfolio.replace(/\W+/g, "_")}` : "vSDI_Streams",
        })} />
      </div>
      <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(340px,1fr))" }}>
        {streams.map((s) => (
          <div key={s.portfolio} className="bg-white rounded-xl border border-slate-200 p-4" data-testid="stream-card">
            <div className="flex items-start justify-between mb-2">
              <div>
                <h3 className="font-semibold text-base" style={{ color: "#1a3666" }}>{s.portfolio}</h3>
                <p className="text-xs text-slate-500 mt-0.5">{s.leads || "—"}</p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold" style={{ color: "#1a3666" }}>{pct(s.avgUtil)}</div>
                <div className="text-[10px] text-slate-500">avg util</div>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-2 text-center text-xs mt-3">
              <div><div className="font-semibold text-emerald-600">{s.optimal}</div><div className="text-slate-400">Optimal</div></div>
              <div><div className="font-semibold text-red-600">{s.overload}</div><div className="text-slate-400">Over</div></div>
              <div><div className="font-semibold text-amber-600">{s.underload}</div><div className="text-slate-400">Under</div></div>
              <div><div className="font-semibold text-slate-500">{s.unassigned}</div><div className="text-slate-400">Idle</div></div>
            </div>
            <div className="mt-3 text-xs text-slate-500 border-t border-slate-100 pt-2">Team: {s.total}</div>
          </div>
        ))}
        {!streams.length && (
          <div className="col-span-full bg-white rounded-xl border border-slate-200 p-6 text-center text-slate-500">
            No streams match this filter.
          </div>
        )}
      </div>
    </div>
  );
}
